import type { TimeInput } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { deriveZoneAbbrev } from './derive-zone-abbrev';
import { formatEventTime } from './format-event-time';
import { isSameDayInZone } from './is-same-day-in-zone';

export interface FormatEventTimeRangeOptions {
    /** Drop the year from both ends ("Sep 22, 12:00 – 1:00 PM CDT"). */
    omitYear?: boolean;
    /** Fallback when neither end is a valid instant. Default "—". */
    placeholder?: string;
}

/** The trailing meridiem ("AM"/"PM") and the space before it, whichever space Intl emits. */
const MERIDIEM = /[\s ]?([AP]M)$/;

/**
 * Format a start→end instant pair in one zone, collapsing a same-day range.
 *
 * Same calendar day (read in `zone`) → the date and zone are written once and a
 * repeated meridiem is dropped: `Sep 22, 12:00 – 1:00 PM CDT`, or
 * `Sep 22, 9:00 AM – 1:00 PM CDT` when the meridiem changes. Different days →
 * both ends in full, the zone once at the end: `Sep 22, 9:00 AM – Sep 24, 4:30 PM CDT`.
 * A range that crosses a DST change keeps a label on each end, because one label
 * would be wrong for one of them.
 *
 * A missing end renders the start alone (and vice versa) rather than a dangling
 * dash; neither end valid → `placeholder`. Same-day detection refuses to guess
 * without a zone ({@link isSameDayInZone} returns null), so a zoneless call
 * falls back to the full two-ended form — longer, never wrong.
 *
 * @param start - The range's first instant.
 * @param end - The range's last instant.
 * @param zone - IANA zone both ends are rendered in (the auction's zone).
 * @param opts - Year and placeholder options.
 * @returns The formatted range.
 * @sideEffect none — pure.
 */
export function formatEventTimeRange(
    start: TimeInput,
    end: TimeInput,
    zone: string | undefined,
    opts: FormatEventTimeRangeOptions = {},
): string {
    const placeholder = opts.placeholder ?? '—';
    const a = toDate(start);
    const b = toDate(end);
    const full = { omitYear: opts.omitYear, placeholder };

    if (!a && !b) return placeholder;
    if (!b) return formatEventTime(start, zone, full);
    if (!a) return formatEventTime(end, zone, full);

    const abbrevA = deriveZoneAbbrev(a, zone);
    const abbrevB = deriveZoneAbbrev(b, zone);
    const sameLabel = abbrevA === abbrevB;

    if (zone && sameLabel && isSameDayInZone({ a, b, zone })) {
        const day = formatEventTime(a, zone, { omitYear: opts.omitYear, dateOnly: true });
        const from = formatEventTime(a, zone, { timeOnly: true, showZone: false });
        const to = formatEventTime(b, zone, { timeOnly: true, showZone: false });
        const fromMeridiem = MERIDIEM.exec(from)?.[1];
        const toMeridiem = MERIDIEM.exec(to)?.[1];
        const fromShown = fromMeridiem && fromMeridiem === toMeridiem ? from.replace(MERIDIEM, '') : from;
        return `${day}, ${fromShown} – ${to}${abbrevB ? ` ${abbrevB}` : ''}`;
    }

    const left = formatEventTime(a, zone, { omitYear: opts.omitYear, showZone: !sameLabel });
    const right = formatEventTime(b, zone, { omitYear: opts.omitYear });
    return `${left} – ${right}`;
}
